/**
 * Payment History Component (T108)
 * Enhanced payment history for customer profile with record payment action
 */

import { useState } from 'react';
import { CreditCard, DollarSign, Filter } from 'lucide-react';
import { useCustomerPayments } from '../../hooks/useCustomers';
import PaymentRecordModal from '../Payments/PaymentRecordModal';

interface PaymentHistoryProps {
  customerId: string;
  customerName?: string;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatCurrency = (amount: string | number) => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

const paymentMethodLabels: Record<string, string> = {
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  CASH: 'Cash',
  CHECK: 'Check',
  BANK_TRANSFER: 'Bank Transfer',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  OTHER: 'Other',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  cash: 'Cash',
  check: 'Check',
  bank_transfer: 'Bank Transfer',
  zelle: 'Zelle',
  venmo: 'Venmo',
  paypal: 'PayPal',
  other: 'Other',
};

const statusConfig: Record<string, { label: string; className: string }> = {
  COMPLETED: { label: 'Completed', className: 'claude-badge claude-badge-success' },
  PENDING: { label: 'Pending', className: 'claude-badge claude-badge-warning' },
  FAILED: { label: 'Failed', className: 'claude-badge claude-badge-error' },
  REFUNDED: { label: 'Refunded', className: 'claude-badge claude-badge-default' },
  PARTIALLY_REFUNDED: { label: 'Partial Refund', className: 'claude-badge claude-badge-warning' },
  CANCELLED: { label: 'Cancelled', className: 'claude-badge claude-badge-default' },
  completed: { label: 'Completed', className: 'claude-badge claude-badge-success' },
  pending: { label: 'Pending', className: 'claude-badge claude-badge-warning' },
  failed: { label: 'Failed', className: 'claude-badge claude-badge-error' },
  refunded: { label: 'Refunded', className: 'claude-badge claude-badge-default' },
  cancelled: { label: 'Cancelled', className: 'claude-badge claude-badge-default' },
};

export default function PaymentHistory({ customerId, customerName }: PaymentHistoryProps) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const { data, isLoading } = useCustomerPayments(customerId, page, 20);

  const allPayments = data?.items || [];
  const pagination = data?.pagination;

  const payments =
    statusFilter === 'all'
      ? allPayments
      : allPayments.filter((p) => p.status.toLowerCase() === statusFilter);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center space-y-4">
          <div className="claude-spinner mx-auto"></div>
          <p className="text-muted-foreground">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter + Actions */}
      <div className="claude-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <label className="text-sm font-medium">Filter by status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="claude-input w-auto"
            >
              <option value="all">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="claude-card">
          <div className="claude-empty-state">
            <CreditCard className="claude-empty-state-icon" />
            <h3 className="claude-empty-state-title">No payments found</h3>
            <p className="claude-empty-state-description">
              {statusFilter !== 'all'
                ? 'No payments match this filter.'
                : "This customer doesn't have any payment history."}
            </p>
          </div>
        </div>
      ) : (
        <div className="claude-card overflow-hidden">
          <table className="claude-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Invoice</th>
                <th>Reference</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => {
                const status = statusConfig[payment.status.toLowerCase()] || statusConfig.pending;
                return (
                  <tr key={payment.id}>
                    <td className="text-muted-foreground">{formatDate(payment.createdAt)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-muted-foreground" />
                        <span>{paymentMethodLabels[payment.method] || payment.method}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 font-medium">
                        <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
                        {formatCurrency(payment.amount)}
                      </div>
                    </td>
                    <td className="text-muted-foreground">{payment.invoiceNumber || '—'}</td>
                    <td className="text-muted-foreground text-xs font-mono">
                      {payment.referenceNumber || '—'}
                    </td>
                    <td>
                      <span className={status.className}>{status.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="claude-button claude-button-secondary text-sm"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={pagination.page >= pagination.pages}
              className="claude-button claude-button-secondary text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Payment Record Modal */}
      <PaymentRecordModal
        isOpen={isRecordModalOpen}
        onClose={() => setIsRecordModalOpen(false)}
        customerId={customerId}
        customerName={customerName}
      />
    </div>
  );
}
